import { AdUnit } from '@pubstack/common/src/adunit'
import { PaginatedResponse, PaginationParams } from '@pubstack/common/src/pagination'
import { sortByName } from '@pubstack/common/src/sort'
import { SiteStacksByProperty } from '@pubstack/common/src/stack'
import { buildUrlWithSearchParams } from '~/utils/url'
import { get, MutationToasts, useMutation, useQuery } from '../api-client'

export const AD_UNITS_QUERY_KEYS = ['adm', 'adunits']
const SITES_STACKS_BY_ADUNITS_QUERY_KEYS = ['adm', 'sites', 'stacks', 'by-adunits']

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
// `options.enabled` filters archived ad units out of the response; the second `enabled` is the query
// gate, so a scope without One never fires the request. Same shape as the PBS hook.
export const useAdUnits = (options?: { enabled?: boolean }, enabled = true) => {
  const url = buildUrlWithSearchParams('/adm/adunits', options || {})
  const queryResult = useQuery<AdUnit[]>({ url, keys: [...AD_UNITS_QUERY_KEYS, options || {}], enabled, select: sortByName })

  return { ...queryResult, data: queryResult.data || [] }
}

/**
 * One page of the ad units list, narrowed and ordered server side.
 *
 * Kept apart from `useAdUnits` rather than replacing it: the stack editor, the bidder seat form and the stack
 * flyout resolve ad units by name and id across the whole scope, so a page would silently break them.
 */
export const useAdUnitsWithPagination = ({
  filters,
  pagination,
  sortBy,
  sortOrder,
  enabled = true,
}: {
  filters?: {
    search?: string | null
    /** The archived ad units instead of the live ones — never both. Owner-gated server side. */
    archived?: boolean
  }
  pagination: PaginationParams
  sortBy?: string
  sortOrder?: 'ascending' | 'descending'
  enabled?: boolean
}) => {
  const url = buildUrlWithSearchParams('/adm/adunits/pagination', { ...filters, ...pagination, sortBy, sortOrder })
  const queryResult = useQuery<PaginatedResponse<AdUnit>>({
    url,
    keys: [...AD_UNITS_QUERY_KEYS, 'pagination', { pagination, sortBy, sortOrder, filters }],
    enabled,
    keepPreviousDataEnabled: true,
  })

  return { ...queryResult, data: queryResult.data || { data: [], pagination: { total: 0, totalPages: 1, ...pagination } } }
}

/** Every ad unit the list filters select, for Export — fetched on click rather than held in the page. */
export const fetchAdUnitsForExport = async (filters: { search?: string | null; archived?: boolean }, sortBy?: string, sortOrder?: 'ascending' | 'descending'): Promise<AdUnit[]> =>
  get<AdUnit[]>(buildUrlWithSearchParams('/adm/adunits/export', { ...filters, sortBy, sortOrder }))

export const useSitesStacksByAdUnits = () => {
  const queryResult = useQuery<SiteStacksByProperty>({ url: '/adm/sites/stacks/by-adunits', keys: SITES_STACKS_BY_ADUNITS_QUERY_KEYS })

  return { ...queryResult, data: queryResult.data || {} }
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
// The archived page's way back. The list page still writes through `adm-api.hook`'s imperative client,
// which reloads by hand; this one is here because the archived page reads through react-query and needs
// the row to leave the list on its own once the update lands.
export const useUpdateAdUnit = (adUnitId: string, toasts: MutationToasts = {}) => {
  return useMutation<AdUnit>({
    method: 'put',
    url: `/adm/adunits/${adUnitId}`,
    collectionKeys: AD_UNITS_QUERY_KEYS,
    resourceId: adUnitId,
    ...toasts,
  })
}
